import { useQuizBrowserBack } from './quizHistoryGuard';
import { QUIZ_ROUTES } from "./quizRoutes";
import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import CommonPageHeader from "../CommonPageHeader";
import StartQuizHeader from "./StartQuizHeader";
import QuizAttemptsCard from "./QuizAttemptsCard";
import { useAttemptedQuizList } from "../../hooks/useAttemptedQuizList";
import {
  buildQuizBackTarget,
  buildQuizFlowState,
  buildQuizListState,
  persistQuizFlowState,
  navigateQuizBack,
  resolveQuizBackTarget,
} from "./quizFlowState";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../../questionBank/QuestionBankList.css";
import "./StartQuiz.css";
import MyWalletSidebar from "../MyWalletSidebar";
import Swal from "sweetalert2";
import { fetchSeriesQuizList } from "../../services/quizSeriesService";
import { useSeriesEnroll } from "../../hooks/useSeriesEnroll";

const StartQuiz = () => {
  const navigate = useNavigate();
  const seriesEnrollMutation = useSeriesEnroll();
  const location = useLocation();
  const flowState = useMemo(() => buildQuizFlowState(location.state), [location.state]);
  const selectedQuiz = flowState?.selectedQuiz || {};
  const listBannerImage = flowState?.listBannerImage || "";
  const selectedStateId = Number(flowState?.selectedState?.state_id || flowState?.state_id || 13);
  const selectedCategoryId = Number(flowState?.category_id || 1);
  const selectedQuizId = Number(selectedQuiz?.quizId || selectedQuiz?.quiz_id || selectedQuiz?.id || 0);
  const selectedRouteKey = Number(flowState?.route_key || 0);
  const listState = useMemo(() => buildQuizListState(flowState), [flowState]);
  const resolvedBackTarget = resolveQuizBackTarget(flowState, QUIZ_ROUTES.start, buildQuizBackTarget(QUIZ_ROUTES.list, listState));
  useQuizBrowserBack(resolvedBackTarget);
  const startQuizFlowState = useMemo(
    () =>
      buildQuizFlowState({
        ...flowState,
        quizBackTarget: resolvedBackTarget,
      }),
    [flowState, resolvedBackTarget]
  );

  useEffect(() => {
    persistQuizFlowState(startQuizFlowState);
  }, [startQuizFlowState]);

  const {
    data: attemptedQuizData,
    isLoading,
    isFetching,
  } = useAttemptedQuizList(
    {
      states_id: selectedStateId,
      category_id: selectedCategoryId,
      quiz_id: selectedQuizId,
    },
    {
      enabled:
        Number.isFinite(selectedStateId) &&
        selectedStateId > 0 &&
        Number.isFinite(selectedCategoryId) &&
        selectedCategoryId > 0 &&
        Number.isFinite(selectedQuizId) &&
        selectedQuizId > 0,
    }
  );
  const isPending = isLoading || isFetching;
  const selectedStateName =
    attemptedQuizData?.stateName ||
    flowState?.selectedState?.state_name ||
    flowState?.stateName ||
    "Himachal Pradesh";
  const categoryName =
    attemptedQuizData?.categoryName || flowState?.selectedQuizOption || "Tests & Quizzes";
  const normalizedQuizData = {
    ...attemptedQuizData,
    statesId: selectedStateId,
    categoryId: selectedCategoryId,
    routeKey: selectedRouteKey,
    stateName: selectedStateName,
    categoryName,
    bannerImage: attemptedQuizData?.bannerImage || listBannerImage,
  };

  const ensureSeriesEnrollment = async () => {
    const seriesId = Number(attemptedQuizData?.seriesId || flowState.series_id || flowState.selectedSeries?.series_id || flowState.selectedSeries?.id || 0);
    if (!seriesId) return true;
    try {
      const { payload } = await fetchSeriesQuizList({ states_id: selectedStateId, category_id: selectedCategoryId, series_id: seriesId, page: 1 });
      if (Number(payload?.series_info?.enrolled_status) === 1) return true;
      const result = await Swal.fire({ title: 'Enroll in this test series', text: 'Enroll before opening a quiz in this series.',
        icon: 'info', showCancelButton: true, confirmButtonText: 'Enroll', confirmButtonColor: '#5b34ea' });
      if (!result.isConfirmed) return false;
      await seriesEnrollMutation.mutateAsync({ states_id: selectedStateId, category_id: selectedCategoryId, series_id: seriesId });
      return true;
    } catch (error) {
      await Swal.fire({ icon: 'error', title: 'Unable to verify enrollment', text: error?.message || 'Please try again.' });
      return false;
    }
  };

  const handleReviewAttempt = (attempt) => {
    const assesmentBatchId = Number(attempt?.assesmentBatchId || 0);

    if (assesmentBatchId <= 0 || attempt?.canReview === false) {
      return;
    }

    const answerSheetFlowState = buildQuizFlowState({
      ...startQuizFlowState,
      quizData: normalizedQuizData,
      assesment_batch_id: assesmentBatchId,
      selectedAttempt: {
        ...attempt,
        quiz_id: Number(attempt?.quizId || selectedQuizId || 0),
        assesment_batch_id: assesmentBatchId,
      },
      quizBackTarget: buildQuizBackTarget(QUIZ_ROUTES.start, startQuizFlowState),
    });

    persistQuizFlowState(answerSheetFlowState);
    navigate(QUIZ_ROUTES.answerSheet, {
      state: answerSheetFlowState,
    });
  };

  return (
    <>
      <style>{`
        .homeDashboard-wrapper .page-content {
          padding-right: 0;
        }
      `}</style>

      <HomeDashboardLayout showHeader={false} variant="common-page-header-layout">
        <div className="qbl-page qbankList-page page-padding">
          <div className="start-quiz qbl-main">
            <div className="hideProfile-page">
              <CommonPageHeader
                embedded
                showLocation={false}
                className="quiz-start-page-header"
                onBack={() => navigateQuizBack(navigate, resolvedBackTarget)}
              />
            </div>

            {!selectedQuizId ? (
              <p className="choose-state__status">Quiz details are unavailable for this item.</p>
            ) : (
              <>
                <StartQuizHeader
                  onBeforeStart={ensureSeriesEnrollment}
                  quizData={normalizedQuizData}
                  isLoading={isPending}
                  navigationState={{
                    ...startQuizFlowState,
                    quizData: normalizedQuizData,
                  }}
                />
                <QuizAttemptsCard
                  attempts={attemptedQuizData?.attempts || []}
                  isLoading={isPending}
                  onReviewAttempt={handleReviewAttempt}
                />
              </>
            )}
          </div>

          <MyWalletSidebar withTopOffset={false} />
        </div>
      </HomeDashboardLayout>
    </>
  );
};

export default StartQuiz;
